import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { fetchCartJson, seedCartCache, isCartJsonObject } from '@theme/cart-api';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

/** @type {Promise<typeof import('@theme/product-jewellery-box')> | undefined} */
let jewelleryBoxModulePromise;

/**
 * Lazily loads the jewellery box module when an add-on exists on the PDP.
 * The add-on is a sibling block (not inside the product form), so search
 * product-form-component and section — not the form element alone.
 *
 * @param {HTMLFormElement | null | undefined} form
 * @returns {Promise<typeof import('@theme/product-jewellery-box').buildJewelleryBoxCartPayload | null>}
 */
async function getBuildJewelleryBoxCartPayload(form) {
  const productForm = form?.closest('product-form-component');
  const sectionRoot = form?.closest('.shopify-section');
  const hasAddon =
    productForm?.querySelector('jewellery-box-addon') ??
    sectionRoot?.querySelector('jewellery-box-addon');

  if (!hasAddon) {
    return null;
  }

  jewelleryBoxModulePromise ||= import('@theme/product-jewellery-box');
  const module = await jewelleryBoxModulePromise;

  return module.buildJewelleryBoxCartPayload;
}

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// How long the stock status row shows the quantity-limit message before the inventory copy returns
const QUANTITY_LIMIT_MESSAGE_DURATION = 2500;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  /** @type {MutationObserver | undefined} */
  #labelObserver;

  /** @type {string} */
  #serverRenderedLabelMarkup = '';

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);

    this.captureLabelMarkup();
    this.#labelObserver = new MutationObserver(this.#restoreLabelMarkup);
    this.#labelObserver.observe(this.refs.addToCartButton, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.#labelObserver?.disconnect();
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /** Remember valid Liquid markup, including the storefront label and basket icon. */
  captureLabelMarkup() {
    const label = this.refs.addToCartButton.querySelector('.add-to-cart-text');
    if (label?.querySelector('.add-to-cart-icon')) {
      this.#serverRenderedLabelMarkup = label.innerHTML;
    }
  }

  #restoreLabelMarkup = () => {
    const label = this.refs.addToCartButton.querySelector('.add-to-cart-text');
    const hasVisibleLoopWidget = this.#hasVisibleLoopWidget();

    if (label && hasVisibleLoopWidget) {
      this.#useBagWording(label);
      return;
    }

    if (
      !label ||
      !this.#serverRenderedLabelMarkup ||
      label.querySelector('.add-to-cart-icon')
    ) {
      return;
    }

    // Loop Subscriptions replaces this with its plain-text addToCartButtonText.
    label.innerHTML = this.#serverRenderedLabelMarkup;
  };

  /** Keep Loop's dynamic CTA meaning while using the storefront's "bag" terminology. */
  #useBagWording(label) {
    const currentText = label.textContent ?? '';
    const bagText = currentText.replace(/\bcart\b/gi, (word) => {
      if (word === word.toUpperCase()) return 'BAG';
      if (word[0] === word[0]?.toUpperCase()) return 'Bag';
      return 'bag';
    });

    if (bagText !== currentText) {
      label.textContent = bagText;
    }
  }

  #hasVisibleLoopWidget() {
    const root = this.closest('.product-details, .shopify-section');
    if (!root) return false;

    return Array.from(
      root.querySelectorAll('[class*="loop-widget"], [id*="loop_subscriptions"], [id*="loop-widget"]')
    ).some((element) => {
      if (!(element instanceof HTMLElement) || element.hidden) return false;
      const rect = element.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return false;
      const style = getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Sets or clears the "adding" state (shows Figma Loading 2 spinner).
   * @param {boolean} adding - Whether the button is in the adding state.
   */
  setAdding(adding) {
    const btn = this.refs.addToCartButton;
    if (adding) {
      btn.classList.add('atc-adding');
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.classList.remove('atc-adding');
      btn.setAttribute('aria-busy', 'false');
    }
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/** Clear stuck loading state when the page is restored via back/forward (bfcache). */
function resetStaleAddToCartButtonStates() {
  document.querySelectorAll('add-to-cart-component').forEach((component) => {
    if (component instanceof AddToCartComponent) {
      component.setAdding(false);
      component.enable();
    }
  });
  document.querySelectorAll('product-form-component form[data-bp-submitting="true"]').forEach((form) => {
    form.dataset.bpSubmitting = 'false';
  });
}

window.addEventListener('pageshow', () => {
  resetStaleAddToCartButtonStates();
});

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {number | undefined} */
  #quantityLimitTimeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    const canAddToCart = this.refs.productFormButtons?.getAttribute('data-can-add-to-cart') === 'true';
    this.#syncPurchasingAvailabilityUi(canAddToCart);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#quantityLimitTimeout) clearTimeout(this.#quantityLimitTimeout);
    this.#abortController.abort();
  }

  /**
   * Updates PDP quantity label from a cart/add or /cart.js payload.
   * @param {object} [cartLike]
   * @returns {Promise<number>}
   */
  async #updateCartQuantityFromPayload(cartLike) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    const variantId = variantIdInput.value.toString();
    let cartQty = 0;

    if (Array.isArray(cartLike?.items)) {
      const cartItem = cartLike.items.find(
        /** @param {any} item */
        (item) => item.variant_id?.toString() === variantId
      );
      cartQty = cartItem ? cartItem.quantity : 0;
    } else if (cartLike?.variant_id?.toString() === variantId) {
      cartQty = Number(cartLike.quantity) || 0;
    }

    if (cartQty === 0 && !Array.isArray(cartLike?.items)) {
      return this.#fetchAndUpdateCartQuantity();
    }

    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }
    this.#updateQuantityLabel(cartQty);
    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const cart = await fetchCartJson();
      const cartItem = cart.items?.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Use public API to update quantity selector
      const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      // Update quantity label if it exists
      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Shop Pay / dynamic checkout must not be hijacked by AJAX add-to-cart.
   * @param {SubmitEvent} event
   * @returns {boolean}
   */
  #isAcceleratedCheckoutSubmit(event) {
    const acceleratedSelector =
      'shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .shopify-payment-button, .shopify-payment-button__button';

    const submitter = event.submitter;
    if (submitter instanceof Element) {
      if (submitter.matches(acceleratedSelector) || submitter.closest(acceleratedSelector)) {
        return true;
      }
    }

    const active = document.activeElement;
    if (active instanceof Element) {
      if (active.matches(acceleratedSelector) || active.closest(acceleratedSelector)) {
        return true;
      }
    }

    const path = event.composedPath?.() ?? [];
    return path.some((node) => node instanceof Element && node.matches(acceleratedSelector));
  }

  /**
   * @returns {HTMLElement & { showDialog?: () => Promise<void> | void } | null}
   */
  #getMakeItASetModal() {
    const productId = this.dataset.productId;
    if (!productId) return null;
    return /** @type {(HTMLElement & { showDialog?: () => Promise<void> | void }) | null} */ (
      document.querySelector(`make-it-a-set-modal[data-product-id="${productId}"]`)
    );
  }

  /**
   * Offer Make It A Set when the selected variant has a set item in the modal map.
   * @returns {boolean}
   */
  #shouldOfferMakeItASet() {
    if (this.dataset.makeItASetBypass === 'true') return false;
    const modal = this.#getMakeItASetModal();
    if (!modal) return false;

    try {
      const item = JSON.parse(modal.dataset.setItems || '{}')[this.refs.variantId?.value || ''];
      if (item) modal.dataset.setItem = item;
      return Boolean(item);
    } catch {
      return false;
    }
  }

  /**
   * Resume Add to bag after Make It A Set modal choice.
   * @param {'original' | 'set'} choice
   * @param {string} [setVariantId]
   */
  submitMakeItASetChoice(choice, setVariantId) {
    const form = /** @type {HTMLFormElement | null} */ (
      this.querySelector('form[data-type="add-to-cart-form"]')
    );
    if (!form) return;

    const variantInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="id"]')
    );
    const previousVariantId = variantInput?.value;

    this.dataset.makeItASetBypass = 'true';

    if (choice === 'set' && setVariantId && variantInput) {
      variantInput.value = setVariantId;
    }

    const restoreVariant = () => {
      if (choice === 'set' && variantInput && previousVariantId) {
        variantInput.value = previousVariantId;
      }
      this.dataset.makeItASetBypass = '';
    };

    const onSettled = () => {
      restoreVariant();
      this.removeEventListener('cart:update', onSettled);
      this.removeEventListener('cart:error', onSettled);
    };

    // Restore PDP variant id after the add attempt finishes (success or error).
    this.addEventListener('cart:update', onSettled, { once: true });
    this.addEventListener('cart:error', onSettled, { once: true });
    setTimeout(restoreVariant, 8000);

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {SubmitEvent} event - The submit event.
   */
  async handleSubmit(event) {
    // Theme event delegation sets event.target to the component (element with on:submit), not the form.
    // Resolve the actual submitted form via submitter or activeElement so we don't return and allow a full-page submit.
    const delegatedTarget = /** @type {HTMLFormElement | null} */ (event?.target);
    const submitterForm = /** @type {HTMLButtonElement | HTMLInputElement | null} */ (event?.submitter)?.form ?? null;
    let form = submitterForm ?? (delegatedTarget instanceof HTMLFormElement ? delegatedTarget : null);
    if (!form && document.activeElement) {
      const activeForm = document.activeElement.closest?.('form');
      if (activeForm && this.contains(activeForm)) form = activeForm;
    }
    // Only handle the add-to-cart form; let other forms submit normally
    if (!form || !this.contains(form) || form.getAttribute('data-type') !== 'add-to-cart-form') {
      return;
    }

    if (this.#isAcceleratedCheckoutSubmit(event)) {
      return;
    }

    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    // Send the add to cart information to the cart
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();

        this.#flashQuantityLimitMessage();
        this.#openCartDrawerForQuantityLimit();

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
        // Keep quantity validation, without inserting a message beside stock status.
        this.#setLiveRegionText(errorMessage);
        this.#timeout = setTimeout(() => {
          this.#clearLiveRegionText();
        }, ERROR_MESSAGE_DISPLAY_DURATION);

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    // Make It A Set: offer set upsell before the first add (unless shopper already chose).
    if (this.#shouldOfferMakeItASet()) {
      const modal = this.#getMakeItASetModal();
      if (modal) {
        try {
          if (typeof modal.showDialog !== 'function') {
            await Promise.race([
              customElements.whenDefined('make-it-a-set-modal'),
              new Promise((resolve) => setTimeout(resolve, 2000)),
            ]);
          }
          const ready = this.#getMakeItASetModal();
          if (ready && typeof ready.showDialog === 'function') {
            await ready.showDialog();
            return;
          }
        } catch {
          // Fall through to normal add if the modal fails to boot.
        }
      }
    }
    this.dataset.makeItASetBypass = '';

    // Prevent duplicate submits while we perform submit-time Budget Pay sync and add-to-cart.
    if (form.dataset.bpSubmitting === 'true') return;
    form.dataset.bpSubmitting = 'true';

    const budgetPayApi = window.BudgetPayCart;
    if (budgetPayApi?.getToggleForForm && budgetPayApi?.syncBudgetPayState) {
      const toggle = budgetPayApi.getToggleForForm(form);
      if (toggle?.active) {
        try {
          await budgetPayApi.syncBudgetPayState(toggle);
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Could not sync Budget Pay with your cart.';
          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = message;
            } else {
              const newTextNode = document.createTextNode(message);
              addToCartTextError.appendChild(newTextNode);
            }
          }
          this.#setLiveRegionText(message);
          form.dataset.bpSubmitting = 'false';
          return;
        }
      }
    }

    const formData = new FormData(form);

    const currentItemSource = formData.get('properties[_itemSource]');
    if (currentItemSource == null || String(currentItemSource).trim() === '') {
      formData.set('properties[_itemSource]', 'FPC');
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const buildJewelleryBoxCartPayload = await getBuildJewelleryBoxCartPayload(form);
    const jewelleryPayload = buildJewelleryBoxCartPayload
      ? buildJewelleryBoxCartPayload(form)
      : null;
    /** @type {BodyInit} */
    let requestBody = formData;
    const fetchHeaders = {
      Accept: 'text/html',
      'X-Requested-With': 'XMLHttpRequest',
    };

    if (jewelleryPayload) {
      /** @type {Record<string, unknown>} */
      const jsonPayload = { ...jewelleryPayload };
      if (cartItemComponentsSectionIds.length) {
        jsonPayload.sections = cartItemComponentsSectionIds.join(',');
      }
      requestBody = JSON.stringify(jsonPayload);
      fetchHeaders['Content-Type'] = 'application/json';
      fetchHeaders.Accept = 'application/json';
    } else {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      Object.assign(fetchHeaders, fetchCfg.headers);
      fetchHeaders.Accept = 'application/json';
    }

    addToCartButtonContainer?.setAdding(true);
    addToCartButtonContainer?.disable();

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      credentials: 'same-origin',
      body: requestBody,
      headers: fetchHeaders,
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {

          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          const delta = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);
          let absoluteItemCount = 0;
          try {
            const cart = await fetchCartJson();
            if (typeof cart?.item_count === 'number') absoluteItemCount = cart.item_count;
          } catch (_) {
            // no-op; keep default
          }
          // Final fallback: session storage (cart-icon cache) + delta
          if (!absoluteItemCount) {
            try {
              const cached = JSON.parse(sessionStorage.getItem('cart-count') || '{}');
              const cachedValue = parseInt(cached?.value ?? '0', 10);
              absoluteItemCount = (Number.isFinite(cachedValue) ? cachedValue : 0) + delta;
            } catch (_) {
              absoluteItemCount = delta;
            }
          }
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              action: 'error',
              source: this.dataset.source || 'product-form-component',
              itemCount: absoluteItemCount,
              delta,
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          if (isCartJsonObject(response)) {
            seedCartCache(response);
          }

          this.dispatchEvent(
            new CartUpdateEvent(response, this.id, {
              action: 'add',
              source: this.dataset.source || 'product-form-component',
              itemCount: typeof response.item_count === 'number' ? response.item_count : undefined,
              sections: response.sections,
            })
          );
          if (typeof window.saveSharedCartToken === 'function') {
            void window.saveSharedCartToken();
          }

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Use add response when possible; shared fetchCartJson dedupes any fallback read.
          void this.#updateCartQuantityFromPayload(response);

        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
        addToCartButtonContainer?.setAdding(false);
        addToCartButtonContainer?.enable();
        form.dataset.bpSubmitting = 'false';
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Add-to-cart rejected because the cart already holds the maximum: shake the stock status row and
   * swap its copy for the limit message, then restore the original inventory text.
   */
  #flashQuantityLimitMessage() {
    const stockStatus = /** @type {HTMLElement | null} */ (
      this.querySelector('.stock-status--low-stock') ?? this.querySelector('.stock-status')
    );
    if (!stockStatus) return;

    stockStatus.classList.remove('stock-status--shake');
    // Restart the CSS animation on each rejected attempt.
    void stockStatus.getBoundingClientRect();
    stockStatus.classList.add('stock-status--shake');

    const limitMessage = this.dataset.quantityLimitMessage;
    if (!limitMessage) return;

    if (this.#quantityLimitTimeout) clearTimeout(this.#quantityLimitTimeout);

    // A repeat attempt while the message is showing must not snapshot the limit copy as the original.
    if (stockStatus.dataset.stockStatusOriginal == null) {
      stockStatus.dataset.stockStatusOriginal = stockStatus.innerHTML;
    }

    stockStatus.textContent = limitMessage;
    stockStatus.classList.add('stock-status--limit-reached');

    this.#quantityLimitTimeout = setTimeout(() => {
      this.#restoreStockStatusMessage(stockStatus);
    }, QUANTITY_LIMIT_MESSAGE_DURATION);
  }

  /**
   * @param {HTMLElement} stockStatus
   */
  #restoreStockStatusMessage(stockStatus) {
    const original = stockStatus.dataset.stockStatusOriginal;
    if (original != null) {
      stockStatus.innerHTML = original;
      delete stockStatus.dataset.stockStatusOriginal;
    }
    stockStatus.classList.remove('stock-status--limit-reached', 'stock-status--shake');
    this.#quantityLimitTimeout = undefined;
  }

  /**
   * Merchant setting: open the mini cart when an add is rejected at the quantity limit so the
   * shopper can check what they already have.
   */
  #openCartDrawerForQuantityLimit() {
    if (this.dataset.openCartOnQuantityLimit !== 'true') return;

    const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    if (!drawer || drawer.querySelector('dialog')?.open) return;

    const open = () => {
      if (!drawer.isConnected || drawer.querySelector('dialog')?.open) return;
      // open() redirects to /cart for sources it does not recognise, so reuse the add-to-cart source.
      void drawer.open(
        new CustomEvent('cart-drawer:quantity-limit', {
          detail: { data: { source: 'product-form-component' } },
        })
      );
    };

    // Without the cart group (theme setting "Load theme scripts when first needed" off) the drawer
    // code loads with the page, as before, so open at once.
    if (customElements.get('cart-drawer-component') || !(/** @type {any} */ (window).__tjcCartGroup)) {
      open();
      return;
    }

    // The drawer code loads on first cart use (the cart group in snippets/scripts.liquid), so this
    // add can come before it: ask for it and open the drawer once it is defined.
    /** @type {any} */ (window).__tjcCartGroup?.load?.();
    customElements.whenDefined('cart-drawer-component').then(open);
  }

  /**
   * Toggle Notify Me / Budget Pay / installments when the selected variant cannot be added to cart.
   * App blocks often sit outside the conditional out-of-stock slot and must sync on variant change.
   * @param {boolean} canAddToCart
   */
  #syncPurchasingAvailabilityUi(canAddToCart) {
    const root = this.closest('.product-details') ?? this.closest('.shopify-section');
    if (!root) return;

    root.querySelectorAll('[id*="notify_me"], [id*="notify-me"], [data-notify-me]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.hidden = canAddToCart;

      const shell = el.closest('.shopify-app-block');
      if (shell instanceof HTMLElement && !shell.closest('.buy-buttons-container')) {
        shell.classList.toggle('product-notify-me--standalone-visible', !canAddToCart);
        shell.hidden = canAddToCart;
      }
    });

    const hideWhenUnavailable = !canAddToCart;
    const purchasingSelectors = [
      '[id*="budget_pay"]',
      '[id*="budget-pay"]',
      '[data-budget-pay-product-installments]',
      '[data-budget-pay-toggle]',
      'budget-pay-product-installments',
      '[class*="loop-widget"]',
      '[id*="loop_subscriptions"]',
    ].join(',');

    root.querySelectorAll(purchasingSelectors).forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const shell = el.closest('.shopify-app-block');
      if (shell instanceof HTMLElement) {
        shell.classList.toggle('product-purchasing-ui--collapsed', hideWhenUnavailable);
      }
      el.hidden = hideWhenUnavailable;
    });

    this.querySelectorAll('.product-installments-container').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.hidden = hideWhenUnavailable;
    });

    const buyButtonsBlock = this.closest('.buy-buttons-block');
    if (buyButtonsBlock instanceof HTMLElement) {
      const hasNotifyInside = Boolean(
        buyButtonsBlock.querySelector('[id*="notify_me"], [id*="notify-me"], [data-notify-me]')
      );
      buyButtonsBlock.classList.toggle(
        'buy-buttons-block--notify-outside',
        hideWhenUnavailable && !hasNotifyInside
      );
    }

    const budgetPayApi = window.BudgetPayCart;
    if (budgetPayApi?.getToggleForForm && budgetPayApi?.syncBudgetPayState) {
      const form = this.querySelector('form');
      const toggle = form ? budgetPayApi.getToggleForForm(form) : null;
      if (toggle?.element instanceof HTMLElement) {
        toggle.element.hidden = hideWhenUnavailable;
      }
    }
  }

  /**
   * Clone the accelerated checkout block from the section response before morph runs.
   * Morph moves nodes out of the parsed document and can mis-match out-of-stock-block with
   * accelerated-checkout-block (both are divs), so checkout is handled separately.
   * @param {ParentNode | null | undefined} source
   * @returns {HTMLElement | null}
   */
  #cloneAcceleratedCheckoutFrom(source) {
    if (!source) return null;
    const block = source.querySelector('.accelerated-checkout-block');
    if (!block) return null;
    const imported = document.importNode(block, true);
    block.remove();
    return /** @type {HTMLElement} */ (imported);
  }

  /**
   * @param {Element | null | undefined} node
   */
  #stripAcceleratedCheckoutFrom(node) {
    node?.querySelector('.accelerated-checkout-block')?.remove();
  }

  /**
   * Insert or remove Shop Pay after buy-buttons morph. Always uses a fresh importNode clone.
   * @param {boolean} canAddToCart
   * @param {HTMLElement | null} checkoutBlock
   */
  #syncAcceleratedCheckout(canAddToCart, checkoutBlock) {
    const container = this.querySelector('.buy-buttons-container');
    container?.querySelector('.accelerated-checkout-block')?.remove();

    if (canAddToCart && checkoutBlock && container) {
      container.appendChild(checkoutBlock);
    }
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * Variant picker passes the full fetched product {@link Document}. Unscoped `querySelector` can match
   * another block's markup (mini-product, recommendations, sidebar). Narrow to this section + product form.
   * @param {Document} htmlDoc
   * @returns {ParentNode}
   */
  #getScopedVariantFragment(htmlDoc) {
    /** @param {string | number} value */
    const escapeAttr = (value) =>
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(String(value))
        : String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

    const sectionId = this.dataset.sectionId;
    const productId = this.dataset.productId;

    if (sectionId && productId) {
      const sectionEl = htmlDoc.getElementById(`shopify-section-${sectionId}`);
      if (sectionEl) {
        const formInSection = sectionEl.querySelector(
          `product-form-component[data-product-id="${escapeAttr(productId)}"]`
        );
        if (formInSection) return formInSection;
        return sectionEl;
      }
    }

    if (productId) {
      const remoteForm = htmlDoc.querySelector(
        `product-form-component[data-product-id="${escapeAttr(productId)}"]`
      );
      if (remoteForm) return remoteForm;
    }

    if (sectionId) {
      const sectionEl = htmlDoc.getElementById(`shopify-section-${sectionId}`);
      if (sectionEl) return sectionEl;
    }

    return htmlDoc.body ?? htmlDoc.documentElement;
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    const htmlDoc = event.detail.data.html;
    const scopedRoot = this.#getScopedVariantFragment(htmlDoc);

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    const currentVariantProps = this.querySelector('.product-form-variant-props');
    const newVariantProps = scopedRoot.querySelector('.product-form-variant-props');
    this.#morphOrUpdateElement(currentVariantProps, newVariantProps, variantId);

    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = scopedRoot.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const canAddToCart =
      scopedRoot.querySelector('.product-form-buttons')?.getAttribute('data-can-add-to-cart') === 'true';
    const importedCheckout = this.#cloneAcceleratedCheckoutFrom(scopedRoot);

    /**
     * When `can_add_to_cart` is false, Liquid omits the add-to-cart snippet from the section HTML.
     * The legacy path only morphed `[ref="addToCartButton"]` when present in the response, so switching
     * to an OOS variant left the old (disabled) ATC in the DOM instead of the out-of-stock / Notify UI.
     * Morph the whole `.buy-buttons-container` from the section fragment (unless the quantity-rules path
     * will morph the entire `.product-form-buttons`, which already includes this subtree).
     * Shop Pay is excluded from morph — see #cloneAcceleratedCheckoutFrom / #syncAcceleratedCheckout.
     */
    if (!isQuantityRulesChanging) {
      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = scopedRoot.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons?.hasAttribute('data-can-add-to-cart')) {
        currentProductFormButtons.setAttribute(
          'data-can-add-to-cart',
          newProductFormButtons.getAttribute('data-can-add-to-cart') ?? 'true'
        );
        this.#syncPurchasingAvailabilityUi(canAddToCart);
      }

      const currentBuyButtonsContainer = this.querySelector('.buy-buttons-container');
      const newBuyButtonsContainer = scopedRoot.querySelector('.buy-buttons-container');

      this.#stripAcceleratedCheckoutFrom(currentBuyButtonsContainer);

      if (currentBuyButtonsContainer && newBuyButtonsContainer) {
        morph(currentBuyButtonsContainer, newBuyButtonsContainer);
      } else {
        this.#morphOrUpdateElement(
          currentBuyButtonsContainer,
          newBuyButtonsContainer,
          this.refs.productFormButtons
        );
      }

      this.#syncAcceleratedCheckout(canAddToCart, importedCheckout);
    }

    const addToCartButtonContainer = /** @type {HTMLElement | null} */ (
      this.querySelector('[ref="addToCartButtonContainer"]')
    );
    if (event.detail.resource && addToCartButtonContainer) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      scopedRoot.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    // Check if quantity rules are appearing/disappearing (causes layout shift)
    if (isQuantityRulesChanging && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = scopedRoot.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        this.#stripAcceleratedCheckoutFrom(currentProductFormButtons);
        morph(currentProductFormButtons, newProductFormButtons);

        this.#syncPurchasingAvailabilityUi(canAddToCart);
        this.#syncAcceleratedCheckout(canAddToCart, importedCheckout);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          scopedRoot.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          newQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = scopedRoot.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    const updatedAddToCartComponent = this.querySelector('[ref="addToCartButtonContainer"]');
    if (updatedAddToCartComponent instanceof AddToCartComponent) {
      updatedAddToCartComponent.captureLabelMarkup();
    }

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();

    this.#syncPurchasingAvailabilityUi(canAddToCart);
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
